<script lang="ts">
  import Category from './Category.svelte'
  import CategorySection from './CategorySection.svelte'
  import LocationsList from './LocationsList.svelte'
  import SearchResults from './SearchResults.svelte'
  import search from '../lib/actions/search-place'
  import filters from '../lib/stores/filters'
  import { onDestroy } from 'svelte'
  import useFilter from '../hooks/use-filter'
  import type { Filter } from '../global'

  const { resetLocationCategory } = filters
  const { categories } = useFilter()
  const { id, areas, placeholder, noResultsFor } = categories.location

  /**
   * Used for search, updated after a timeout to prevent searching
   * for a place on each key press.
   */
  let searchQuery = ''

  /**
   * Used only to clear user's search input, updated on each key press.
   */
  let searchValue = ''

  let searchTimeout = undefined
  let searchResults: Array<Filter.Locations> = []

  const debounce = (value: string) => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      searchQuery = value
    }, 400)
  }

  const chooseFirstMatch = () => {
    let locations = searchResults.filter(({ cities }) => cities.length > 0)

    if (locations.length > 0) {
      //there are regions with cities, take the first city
      $filters.place = locations[0].cities[0].seostring
      return
    }

    locations = searchResults.filter(({ region }) => !!region)

    if (locations.length > 0) {
      //found only regions, take the first region
      $filters.place = -locations[0].region.key
      return
    }
  }

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault() // prevent the form from being submitted

      if (searchResults.length > 0) {
        chooseFirstMatch()
      }
    }
  }

  const onKeyup = (event: KeyboardEvent) => {
    const { value } = event.currentTarget as HTMLInputElement
    debounce(value.trim())
  }

  const clearSearchQuery = () => {
    searchQuery = ''
    searchValue = ''
  }

  onDestroy(() => clearTimeout(searchTimeout))

  $: if (searchQuery) {
    searchResults = search(searchQuery, areas)
  }
</script>

<Category
  {id}
  on:open={clearSearchQuery}
  on:close={clearSearchQuery}
  on:reset={resetLocationCategory}
>
  <CategorySection>
    <div class="relative mb-3 flex">
      <input
        type="text"
        autocomplete="off"
        aria-label={placeholder}
        {placeholder}
        class="input"
        on:keydown={onKeydown}
        on:keyup={onKeyup}
        bind:value={searchValue}
      />
      {#if searchValue}
        <button
          type="button"
          class="absolute right-0 table-cell px-2 text-gray-600"
          on:click={clearSearchQuery}
        >
          <span class="text-3xl !leading-none">&times;</span>
        </button>
      {/if}
    </div>

    {#if searchQuery}
      <SearchResults areas={searchResults} query={searchQuery} {noResultsFor} />
    {:else}
      <LocationsList {areas} />
    {/if}
  </CategorySection>
</Category>
